import './App.css';
import {
  Card,
  Carousel,
  Col,
  ConfigProvider,
  Row,
  Space,
  Statistic,
  Typography,
} from 'antd';
import 'react-quill/dist/quill.snow.css';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
const { Countdown } = Statistic;
const { Title, Text } = Typography;

// --- 新的更真实的银杏叶 SVG ---
const GinkgoLeafIcon = ({ className, style }: { className?: string, style?: React.CSSProperties }) => (
  <svg className={className} style={style} viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="5435" width="200" height="200"><path d="M977.646268 564.414569c10.075205-40.24508-1.045146-73.647938-1.045145-73.647938s17.237937-54.263968-1.825522-71.181395c-8.653807-7.678338 23.689971-57.62237-66.736041-89.088225-6.298745-40.022115-45.094556-63.210416-90.272723-74.846372 19.815964-45.28965-19.509388-109.391923-100.487282-102.521832 10.507199-67.265581-65.718766-75.250495-95.205811-76.407123s-40.704944-42.809171-61.886565-35.953014-107.301632-4.180583-107.301632-4.180583-44.077281-49.30301-70.860883-33.444665c-10.535069 7.511114-56.38213 36.524361-78.03755 40.412303-12.360591 2.201774-40.412303-26.477026-52.229418 10.56294-4.710124 14.827135-24.456411-11.844985-55.253373 28.609123-46.529889 61.259477 242.780394 321.542579 152.647023 545.426737 0 0-303.524266 357.049664-321.570449 353.956033s-5.142117 41.805831-5.142117 41.80583S48.623165 1018.704595 57.179425 991.586546c26.212256-56.841994 318.713717-354.680667 319.11784-354.806084 62.875969-9.894047 140.481526 29.94691 439.421086 283.527143 16.722332 14.158241 76.644023 40.663138 87.541409-2.048486 6.967638-26.477026 40.314756-101.239786 21.571809-153.357722-0.013935 0 139.422445-37.165383 52.814699-200.486828z" fill="#F4A300" p-id="5436"></path></svg>
);

// 定义更复杂的叶子状态
interface Leaf {
  id: number;
  left: number;       // 水平位置 %
  size: number;       // 大小 px
  fallDuration: number;   // 下落时长 s
  swayDuration: number;   // 摇摆周期时长 s
  swayDelay: number;      // 摇摆延迟 s
  spinDuration: number;   // 自旋一圈时长 s
  spinDirection: 1 | -1;  // 自旋方向 (1: 顺时针, -1: 逆时针)
}

const Home = () => {
  const birthday = Number(new Date('2027/2/19'));
  // 叶子状态管理
  const [leaves, setLeaves] = useState<Leaf[]>([]);
  const [isHovering, setIsHovering] = useState(false);
  const timerRef = useRef<number | null>(null);
  const leafIdCounter = useRef(0);
  const [position, setPosition] = useState({
    x: 0,
    y: 0,
  });
  useEffect(() => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);
  useEffect(() => {
    if (isHovering) {
      // 生成频率稍微加快一点点，因为现在动画更顺滑了
      timerRef.current = window.setInterval(() => {
        addLeaf();
      }, 200);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isHovering]);

  const addLeaf = () => {
    const size = Math.random() * 25 + 15; // 15px - 40px
    const newLeaf: Leaf = {
      id: leafIdCounter.current++,
      left: Math.random() * 95,           // 0% - 95%
      size: size,
      fallDuration: Math.random() * 4 + 4, // 下落变慢: 4s - 8s，看起来更悠闲
      swayDuration: Math.random() * 2 + 3, // 摇摆周期: 3s - 5s
      swayDelay: Math.random() * -5,       // 负延迟让摇摆起始点随机
      spinDuration: Math.random() * 5 + 5, // 自旋一周: 5s - 10s (较慢的持续旋转)
      spinDirection: Math.random() > 0.5 ? 1 : -1, // 随机方向
    };

    setLeaves((prev) => [...prev, newLeaf]);
  };

  const removeLeaf = (id: number) => {
    setLeaves((prev) => prev.filter((leaf) => leaf.id !== id));
  };
  // 渲染数据
  return (
    <>
      <ConfigProvider
        theme={{
          components: {
            Carousel: {
              arrowSize: 48,
            },
          },
        }}>
      <Helmet>
        <meta
          name="description"
          content="嘿！朋友！这里有清楚可爱的北极狐~"
        />     
        <meta name="keywords" content="星海玲奈,VUP,北极狐,小狐凃,玲奈的dd站,棉花糖投稿" />
      </Helmet>
        <Space direction='vertical' style={{ width: '100%' }}>
          <Title level={2}>欢迎来到狐哩狐哩——玲奈的DD站！喵</Title>
          <Text style={{ fontSize: 20 }}>
            玲奈呀，是一只清楚可爱的北极狐。这里的话记录着玲奈的百科
            <s>（黑历史）</s>
            、可爱的表情包、录播和歌切、还有大家给玲奈的棉花糖投稿~
          </Text>
          <Text style={{ fontSize: 20 }}>
            银杏！小木屋！大冒险！钓鱼的玲奈今天能否吃上烤鱼呢！
          </Text>
        </Space>
        <Row justify='center' align='top' gutter={[20, 10]}>
          {/* <Col
            xs={24}
            lg={12}
            onPointerMove={e => {
              setPosition({
                x: (e.clientX / window.screen.width) * 50 - 25,
                y:
                  e.clientY / window.screen.height -
                  Math.sqrt(e.clientX) / 5 -
                  2,
              });
            }}
            onPointerLeave={e => {
              setPosition({ x: 0, y: 0 });
            }}
            style={{
              position: 'relative',
              overflow: 'hidden',
              aspectRatio: 1,
              borderRadius: '15px',
            }}> */}
            {/* <img
              src='/board.png'
              alt='星海玲奈'
              width={'100%'}
              style={{
                position: 'absolute',
                bottom: 0,
                zIndex: 3,
                transform: `translate(${position.x}px, ${position.y}px)`,
                overflow: 'hidden',
                transition: '0.1s',
              }}></img>
            <img
              src='/flower.png'
              alt='花花'
              width={'100%'}
              style={{
                position: 'absolute',
                bottom: 0,
                zIndex: 2,
              }}></img>
            <img
              src='/background.png'
              alt='花花'
              width={'100%'}
              style={{
                position: 'absolute',
                bottom: 0,
                zIndex: 1,
                borderRadius: 15,
              }}></img>
            <img
              src='/naonao.png'
              alt='闹闹'
              width={'100%'}
              style={{
                position: 'absolute',
                transform: `translate(${position.x * 1.1 + 5}px, ${
                  position.y * 0.9
                }px)`,
                bottom: 0,
                zIndex: 6,
                transition: '0.1s',
              }}></img>
            <img
              src='/sanlun.png'
              alt='三轮'
              width={'100%'}
              style={{
                position: 'absolute',
                transform: `translate(${position.x * 0.5}px, ${
                  position.y * 0.7
                }px)`,
                bottom: 0,
                zIndex: 5,
                transition: '0.1s',
              }}></img> */}


{/* 夏日主题 */}
              {/* <img
              src='/lynne-summer.gif'
              alt='星海玲奈'
              width={'50%'}
              style={{
                position: 'absolute',
                bottom: 150,
                left: 20,
                zIndex: 3,
                transform: `translate(${position.x}px, ${position.y - 50}px)`,
                overflow: 'hidden',
                transition: '0.1s',
              }}></img>
              <img
              src='/board-summer.png'
              alt='花花'
              width={'100%'}
              style={{
                position: 'absolute',
                bottom: 0,
                zIndex: 1,
                borderRadius: 15,
              }}></img> */}

          {/* </Col> */}
          <Col
            xs={24}
            lg={12}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              position: 'relative',
              overflow: 'hidden',
              aspectRatio: 16/9,
              borderRadius: '15px',
            }}
          >
            {/* --- 核心修改：嵌套动画结构 --- */}
            {leaves.map((leaf) => (
              // 1. 外层容器：控制下落 (Fall)
              <div
                key={leaf.id}
                className="ginkgo-leaf-wrapper"
                style={{
                  left: `${leaf.left}%`,
                  width: leaf.size,
                  height: leaf.size,
                  animationDuration: `${leaf.fallDuration}s`,
                }}
                onAnimationEnd={() => removeLeaf(leaf.id)} // 仅当下落结束时移除
              >
                {/* 2. 中层容器：控制摇摆 (Sway) */}
                <div 
                  className="ginkgo-leaf-sway"
                  style={{
                    animationDuration: `${leaf.swayDuration}s`,
                    animationDelay: `${leaf.swayDelay}s`
                  }}
                >
                  {/* 3. 内层 SVG：控制自旋 (Spin) */}
                  <GinkgoLeafIcon 
                    className="ginkgo-leaf-svg"
                    style={{
                        // 根据方向选择对应的动画名
                        animationName: leaf.spinDirection > 0 ? 'leaf-spin-cw' : 'leaf-spin-ccw',
                        animationDuration: `${leaf.spinDuration}s`,
                    }}
                  />
                </div>
              </div>
            ))}
            {/* --- 嵌套结构结束 --- */}
            {
              isHovering && <img
              src='/ringing.gif'
              alt='星海玲奈'
              width={'15%'}
              style={{
                position: 'absolute',
                bottom: '42%',
                left: '30%',
                zIndex: 3,
              }}
            ></img>
            }
            
            <img
              src='/lynne-autumn.jpg'
              alt='花花'
              width={'100%'}
              style={{
                position: 'absolute',
                bottom: 0,
                zIndex: 1,
                borderRadius: 15,
              }}
            ></img>
          </Col>
          <Col xs={24} lg={12}>
            <Row justify='center' align='top' gutter={[20, 10]}>
              <Col span={24}>
                <Carousel
                  arrows
                  autoplay={{ dotDuration: true }}
                  draggable
                  className='slide'>
                  <div>
                    <img
                      className='rollimg'
                      src='https://nekoda.cn/usr/uploads/13301209b5e870da5b5af5efc9f3306712e923da-1741013406.jpg'
                      alt='生日会'
                    />
                  </div>
                  <div>
                    <img
                      className='rollimg'
                      src='https://nekoda.cn/usr/uploads/Image_1723768154359-1739539558.jpg'
                      alt='蛋包饭'
                    />
                  </div>
                  <div>
                    <img
                      className='rollimg'
                      src='https://nekoda.cn/usr/uploads/17208868700539a299d067f344702f202aea78d0ddc02.jpg'
                      alt='wink'
                    />
                  </div>
                </Carousel>
              </Col>
              <Col span={24} style={{ height: '100%' }}>
                <Card variant='borderless' hoverable style={{ height: '100%' }}>
                  <Countdown
                    title='距离玲奈六周年生日还有~'
                    value={birthday}
                    format='D天H时m分s秒'
                  />
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </ConfigProvider>
    </>
  );
};

export default Home;
